/**
 * DI Tank Exchange — public catalog under /marketplace/di-tank-exchange
 *
 * Pricing source: backend/data/di_tank_catalog.json — exposed via
 * GET /api/catalog/di-tanks. This catalog is the single source of truth
 * shared with /solutions/water-treatment and (going forward) email/PDF
 * templates so prices never drift between channels.
 *
 * Items here are added to the same global Quote Cart used by Janitorial,
 * Surgical, and other catalog pages — but tagged `source: "di-tank-exchange"`
 * so the admin Sourcing Desk can route the cart to dispatch when checked out.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShoppingCart, Droplets, Truck, Recycle, Shield,
  Phone, CheckCircle2, Plus, Minus, BadgeCheck, Atom, RotateCw, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import SiteHeader from "@/components/SiteHeader";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function FeatureStrip() {
  const items = [
    { icon: Truck, t: "Next-Day Exchange", d: "Same-day for emergencies" },
    { icon: Recycle, t: "No Chemicals On-Site", d: "We handle regeneration" },
    { icon: Shield, t: "cGMP / ISO 9001", d: "Resin tested at multiple points" },
    { icon: BadgeCheck, t: "AAMI ST108-Ready", d: "Compliance documentation included" },
  ];
  return (
    <div className="grid md:grid-cols-4 gap-3 mb-10">
      {items.map((it) => (
        <div key={it.t} className="bg-white border border-slate-200 rounded-xl p-4 hover:border-cyan-400 hover:shadow-md transition-all">
          <div className="w-9 h-9 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center mb-2">
            <it.icon className="w-4 h-4" />
          </div>
          <p className="font-bold text-sm text-slate-900">{it.t}</p>
          <p className="text-xs text-slate-500 mt-0.5">{it.d}</p>
        </div>
      ))}
    </div>
  );
}

function DITankProductCard({ product }) {
  const { addItem, cart, setShowCart } = useQuoteCart();
  const [qty, setQty] = useState(1);
  const inCart = cart.find((c) => c.erp === product.erp);
  const imageUrl = `${API_BASE}/api/static/backorders/${product.image_filename}`;

  const handleAdd = () => {
    addItem({
      erp: product.erp,
      name: product.short_name,
      image: imageUrl,
      source: "di-tank-exchange",
      category: "di-tank-exchange",
      unit_price: product.unit_price,
      list_price: product.list_price,
      pack: product.capacity,
    }, qty);
    setShowCart(true);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all group" data-testid={`di-product-${product.erp}`}>
      <div className="relative">
        <img
          src={imageUrl}
          alt={product.name}
          className="w-full h-52 object-cover bg-slate-50 group-hover:scale-105 transition-transform duration-300"
          onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=400"; }}
        />
        {product.badge && (
          <Badge className="absolute top-3 right-3 bg-amber-500 text-white shadow">
            {product.badge}
          </Badge>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <Droplets className="w-4 h-4 text-cyan-600" />
          <p className="text-xs text-cyan-700 font-bold uppercase tracking-wider">Sterile Mate · DI Exchange</p>
        </div>
        <h3 className="font-bold text-lg text-slate-900 mb-1">{product.name}</h3>
        <p className="text-xs text-slate-500 font-semibold mb-3">{product.best_for}</p>
        <p className="text-sm text-slate-600 mb-4 leading-snug">{product.description}</p>

        <ul className="space-y-1.5 mb-4">
          {product.features.slice(0, 3).map((f) => (
            <li key={f} className="flex items-start gap-2 text-xs text-slate-600">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <div className="flex items-baseline gap-2 mb-3 pb-3 border-b border-slate-100">
          <span className="text-3xl font-bold text-slate-900">${product.unit_price.toFixed(2)}</span>
          <span className="text-xs text-slate-500">per tank exchange</span>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="px-2 py-2 text-slate-500 hover:bg-slate-50"
              data-testid={`di-qty-minus-${product.erp}`}
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <Input
              type="number"
              min="1"
              max="50"
              value={qty}
              onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1", 10) || 1))}
              className="w-14 h-9 border-0 text-center text-sm focus-visible:ring-0"
              data-testid={`di-qty-${product.erp}`}
            />
            <button
              type="button"
              onClick={() => setQty((q) => Math.min(50, q + 1))}
              className="px-2 py-2 text-slate-500 hover:bg-slate-50"
              data-testid={`di-qty-plus-${product.erp}`}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          <Button
            onClick={handleAdd}
            className={`flex-1 ${inCart ? "bg-emerald-600 hover:bg-emerald-700" : "bg-cyan-600 hover:bg-cyan-700"}`}
            data-testid={`di-add-to-cart-${product.erp}`}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {inCart ? `In Cart (${inCart.qty}) — Add ${qty}` : "Add to Cart"}
          </Button>
        </div>

        <p className="text-[11px] text-slate-400 mt-3 text-center">
          Fuel surcharge per service trip applies. One-time setup fee for new accounts.
        </p>
      </div>
    </div>
  );
}

export default function DITankExchangeCatalogPage() {
  const [products, setProducts] = useState([]);
  const [startingAt, setStartingAt] = useState(115);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/api/catalog/di-tanks`)
      .then((res) => {
        if (cancelled) return;
        setProducts(res.data?.products || []);
        if (res.data?.starting_at) setStartingAt(res.data.starting_at);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <SiteHeader />
      <MarketplaceCategoryNav currentCategory="di-tank" />
      <MarketplaceBreadcrumb trail={[{ label: "DI Tank Exchange" }]} />
      {/* Header bar */}
      <header className="bg-gradient-to-br from-cyan-800 via-slate-900 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/marketplace" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Marketplace</span>
          </Link>
          <a href="tel:2814563458" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
            <Phone className="w-4 h-4" /> 281.456.3458
          </a>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-500/15 border border-cyan-400/30 rounded-full mb-5">
              <Droplets className="w-4 h-4 text-cyan-300" />
              <span className="text-cyan-200 text-xs font-bold uppercase tracking-widest">DI Tank Exchange Program</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
              Deionized Water On Demand —<br />
              <span className="text-cyan-300">Cancel Anytime, No Contracts.</span>
            </h1>
            <p className="text-lg text-slate-300 mb-6 max-w-2xl">
              Add tank exchanges to your cart, submit a PO, and we&rsquo;ll dispatch a certified contractor to swap your exhausted tanks for freshly regenerated, performance-verified replacements. Built for sterile processing, endoscope reprocessing, dialysis, and lab support.
            </p>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              {["USP / CLSI", "ISO 3696", "AAMI ST108", "cGMP / ISO 9001"].map((b) => (
                <span key={b} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-cyan-200">{b}</span>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <FeatureStrip />

        {/* Tank size cards */}
        <div className="text-center mb-8">
          <p className="text-xs font-bold uppercase tracking-widest text-cyan-700 mb-1">Choose your tank size</p>
          <h2 className="text-2xl font-bold text-slate-900">Three tanks. One program. Zero hassle.</h2>
          <p className="text-sm text-slate-500 mt-1">Starting at ${Math.floor(startingAt)}/tank · Add to cart and we&rsquo;ll handle the rest.</p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500" data-testid="di-tank-products-loading">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading catalog…
          </div>
        ) : error ? (
          <div className="text-center py-16 text-slate-500" data-testid="di-tank-products-error">
            Catalog temporarily unavailable. Please call <a href="tel:2814563458" className="text-cyan-700 font-semibold">281.456.3458</a>.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-testid="di-tank-products">
          {products.map((p) => <DITankProductCard key={p.erp} product={p} />)}
        </div>
        )}

        {/* Tech-spec band */}
        <div className="bg-slate-900 rounded-2xl px-8 py-10 text-white mb-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-cyan-400 mb-2">Regenerated to Your Spec</p>
              <h3 className="text-2xl font-bold mb-3">Mixed Bed · Dual Bed · GAC Pre-Treatment</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Our portable DI tanks are regenerated under strict QC aligned with cGMP and ISO 9001 protocols. We use only premium-grade ion-exchange resins, tested at multiple points during regeneration for optimal effluent quality and consistent throughput.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: Atom, label: "Mixed Bed", spec: "≤ 18.2 MΩ" },
                { icon: RotateCw, label: "Dual Bed", spec: "≤ 200K Ω" },
                { icon: Shield, label: "GAC", spec: "Cl removal" },
              ].map((s) => (
                <div key={s.label} className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                  <s.icon className="w-5 h-5 text-cyan-300 mx-auto mb-2" />
                  <p className="text-xs font-bold">{s.label}</p>
                  <p className="text-[10px] text-cyan-200 mt-0.5">{s.spec}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Need help / link to assessment */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center" data-testid="di-help-cta">
          <h3 className="text-lg font-bold text-slate-900 mb-1">Not sure which tank you need?</h3>
          <p className="text-sm text-slate-500 mb-4">Talk to a Sterile Mate water-treatment engineer or schedule a free site assessment.</p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button asChild className="bg-cyan-600 hover:bg-cyan-700">
              <Link to="/solutions/water-treatment#di-tank-exchange" data-testid="di-cta-water-treatment-link">
                Learn more about the program
              </Link>
            </Button>
            <Button asChild variant="outline">
              <a href="tel:2814563458"><Phone className="w-4 h-4 mr-2" /> 281.456.3458</a>
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-4 max-w-2xl mx-auto">
            All prices are subject to change. Fuel surcharge will apply per service trip. A one-time equipment setup &amp; installation fee applies for new accounts per location. Cancel anytime — no long-term contracts.
          </p>
        </div>
      </main>
    </div>
  );
}
